import React, { useEffect, useMemo, useState } from "react";
import { ModalHeader, ModalShell } from "components/ui/ModalFrame";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import Button from "components/ui/Button";
import LoadingMark from "components/ui/LoadingMark";
import Icon from "components/AppIcon";
import { MODAL_LAYER } from "constants/modalLayers";
import { MODAL_STICKY_FOOTER_END } from "constants/layoutSpacing";
import customerService from "services/customerService";
import {
  normalizeCustomersList,
  unwrapCustomerDetail,
  getCustomerId,
  getCustomerNome,
  getCustomerCpf,
} from "utils/customerApiHelpers";
import {
  getRepeatableSectionFields,
  mapCustomerRecordToRepeatableRow,
} from "utils/customerToRepeatablePessoaRow";
import { cn } from "utils/cn";
import { maskCpf } from "utils/masks";

const AddCustomersFromListModal = ({
  isOpen,
  onClose,
  targets = [],
  formSchema,
  onConfirm,
}) => {
  const [selectedGroupKey, setSelectedGroupKey] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState(null);
  const [filterNome, setFilterNome] = useState("");
  const [filterCpf, setFilterCpf] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [listFetched, setListFetched] = useState(false);

  const tipoOptions = useMemo(
    () =>
      targets.map((t) => ({
        value: t.groupKey,
        label: t.label,
      })),
    [targets]
  );

  useEffect(() => {
    if (!isOpen) return;
    setSelectedGroupKey("");
    setCustomers([]);
    setLoadingList(false);
    setListError(null);
    setFilterNome("");
    setFilterCpf("");
    setSelectedIds([]);
    setSubmitError("");
    setListFetched(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !selectedGroupKey || listFetched) return;

    let cancelled = false;

    const load = async () => {
      setLoadingList(true);
      setListError(null);

      const result = await customerService.getCustomers();

      if (cancelled) return;

      if (!result.success) {
        setListError(
          typeof result.error === "string"
            ? result.error
            : "Não foi possível carregar os clientes."
        );
        setCustomers([]);
        setLoadingList(false);
        setListFetched(true);
        return;
      }

      setCustomers(normalizeCustomersList(result.data));
      setListFetched(true);
      setLoadingList(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedGroupKey, listFetched]);

  const filteredCustomers = useMemo(() => {
    const fn = filterNome.trim().toLowerCase();
    const fc = filterCpf.replace(/\D/g, "");

    return customers.filter((c) => {
      const nome = getCustomerNome(c).toLowerCase();
      const cpf = getCustomerCpf(c).replace(/\D/g, "");
      if (fn && !nome.includes(fn)) return false;
      if (fc && !cpf.includes(fc)) return false;
      return true;
    });
  }, [customers, filterNome, filterCpf]);

  const toggleSelect = (id) => {
    if (!id) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAdicionar = async () => {
    setSubmitError("");
    if (!selectedGroupKey) {
      setSubmitError("Selecione o tipo (Vendedor ou Comprador).");
      return;
    }
    if (selectedIds.length === 0) {
      setSubmitError("Selecione ao menos um cliente.");
      return;
    }

    const sectionFields = getRepeatableSectionFields(
      formSchema,
      selectedGroupKey
    );
    if (!sectionFields.length) {
      setSubmitError("Seção do formulário não encontrada.");
      return;
    }

    setSubmitting(true);

    try {
      const results = await Promise.all(
        selectedIds.map((id) => customerService.getCustomer(id))
      );

      const failed = results.find((r) => !r.success);
      if (failed) {
        setSubmitError(
          typeof failed.error === "string"
            ? failed.error
            : "Erro ao carregar dados de um ou mais clientes."
        );
        return;
      }

      const rows = results.map((r) => {
        const detail = unwrapCustomerDetail(r.data);
        return mapCustomerRecordToRepeatableRow(detail, sectionFields);
      });

      onConfirm(selectedGroupKey, rows);
      onClose();
    } catch (e) {
      setSubmitError(e?.message || "Erro ao adicionar clientes.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const showFiltersAndList = Boolean(selectedGroupKey);

  return (
    <ModalShell
      zClass={MODAL_LAYER.standard}
      backdropClassName="bg-black/50"
      panelClassName={cn(
        "bg-card rounded-xl shadow-elevation-lg flex flex-col",
        "w-[min(100vw-1.5rem,42rem)] max-w-2xl",
        "min-h-[min(480px,78svh)] max-h-[min(90vh,760px)]"
      )}
    >
      <ModalHeader title="Adicionar cliente" onClose={onClose} />

      {/* Tipo outside scroll + high z-index so list (below in DOM) does not cover the dropdown */}
      <div className="flex-shrink-0 px-4 pt-1 pb-4 relative z-[60]">
        <Select
          label="Tipo"
          placeholder="Selecione onde adicionar"
          options={tipoOptions}
          value={selectedGroupKey}
          onChange={(v) => {
            setSelectedGroupKey(v || "");
            setSelectedIds([]);
          }}
          disabled={submitting}
        />
      </div>

      <div className="flex-1 min-h-0 flex flex-col border-t border-border relative z-0 isolate">
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        {showFiltersAndList && loadingList && (
          <div className="flex justify-center py-4">
            <LoadingMark size="xs" message="Carregando clientes…" />
          </div>
        )}

        {showFiltersAndList && !loadingList && listError && (
          <p className="text-sm text-destructive">{listError}</p>
        )}

        {showFiltersAndList && !loadingList && !listError && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Filtrar por nome"
                value={filterNome}
                onChange={(e) => setFilterNome(e.target.value)}
                disabled={submitting}
                placeholder="Nome"
              />
              <Input
                label="Filtrar por CPF"
                value={filterCpf}
                onChange={(e) => setFilterCpf(e.target.value)}
                disabled={submitting}
                placeholder="Somente números"
              />
            </div>

            {filteredCustomers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                Nenhum cliente encontrado com os filtros atuais.
              </p>
            ) : (
              <ul className="border border-border rounded-md divide-y divide-border max-h-[min(52vh,440px)] min-h-[200px] overflow-y-auto">
                {filteredCustomers.map((c) => {
                  const id = getCustomerId(c);
                  const nome = getCustomerNome(c);
                  const cpf = getCustomerCpf(c);
                  const selected = id && selectedIds.includes(id);

                  return (
                    <li key={id || `${nome}-${cpf}`}>
                      <button
                        type="button"
                        disabled={!id || submitting}
                        onClick={() => toggleSelect(id)}
                        className={cn(
                          "w-full text-left px-3 py-3 flex items-start gap-3 transition-colors",
                          selected
                            ? "bg-primary/10 border-l-4 border-l-primary"
                            : "hover:bg-muted/50 border-l-4 border-l-transparent",
                          (!id || submitting) && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <span
                          className={cn(
                            "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border",
                            selected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border"
                          )}
                          aria-hidden
                        >
                          {selected && <Icon name="Check" size={14} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="font-medium block truncate">
                            {nome || "—"}
                          </span>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            CPF: {cpf ? maskCpf(cpf) : "—"}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {selectedIds.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {selectedIds.length} cliente(s) selecionado(s).
              </p>
            )}
          </>
        )}

        {submitError && (
          <p className="text-sm text-destructive" role="alert">
            {submitError}
          </p>
        )}
        </div>
      </div>

      <div
        className={cn(
          MODAL_STICKY_FOOTER_END,
          "flex-shrink-0 border-t border-border bg-card rounded-b-xl relative z-40"
        )}
      >
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={submitting}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          onClick={handleAdicionar}
          disabled={
            submitting ||
            !selectedGroupKey ||
            selectedIds.length === 0 ||
            loadingList
          }
        >
          {submitting ? "Adicionando…" : "Adicionar"}
        </Button>
      </div>
    </ModalShell>
  );
};

export default AddCustomersFromListModal;
